// ── CONTACT PLATFORM DETECTION ───────────────────────────────────────────────
// Turns a contact URL into the NAME of the service behind it, so a profile can
// show "Telegram" instead of "t.me/gev". A designer's contact row is answering
// "how do I reach this person" — the service is the answer; the URL is just how
// the browser gets there.
//
// Matching is on the HOST only, never the whole string. A path can contain
// anything ("mysite.com/my-instagram-feed"), and matching on that would label
// links by whatever words happen to appear in them.

const PLATFORMS = [
  // ── messengers ──
  // Subdomains count: web.telegram.org is Telegram too, so every pattern is
  // anchored as "start-or-dot" rather than start-only.
  { key: 'telegram',  name: 'Telegram',  icon: '✈️',  hosts: [/^t\.me$/, /(^|\.)telegram\.(me|org|dog)$/] },
  { key: 'whatsapp',  name: 'WhatsApp',  icon: '💬', hosts: [/^wa\.me$/, /(^|\.)whatsapp\.com$/, /^chat\.whatsapp\.com$/] },
  { key: 'viber',     name: 'Viber',     icon: '📳', hosts: [/(^|\.)viber\.com$/] },
  { key: 'messenger', name: 'Messenger', icon: '💬', hosts: [/^m\.me$/, /(^|\.)messenger\.com$/] },
  { key: 'signal',    name: 'Signal',    icon: '🔒', hosts: [/^signal\.me$/, /(^|\.)signal\.org$/] },
  { key: 'skype',     name: 'Skype',     icon: '📞', hosts: [/(^|\.)skype\.com$/] },
  { key: 'discord',   name: 'Discord',   icon: '🎮', hosts: [/(^|\.)discord\.(gg|com)$/] },

  // ── social ──
  { key: 'instagram', name: 'Instagram', icon: '📷', hosts: [/(^|\.)instagram\.com$/, /^instagr\.am$/] },
  { key: 'facebook',  name: 'Facebook',  icon: '👤', hosts: [/(^|\.)facebook\.com$/, /^fb\.(com|me)$/] },
  { key: 'tiktok',    name: 'TikTok',    icon: '🎵', hosts: [/(^|\.)tiktok\.com$/] },
  { key: 'x',         name: 'X',         icon: '𝕏',  hosts: [/^x\.com$/, /(^|\.)twitter\.com$/] },
  { key: 'linkedin',  name: 'LinkedIn',  icon: '💼', hosts: [/(^|\.)linkedin\.com$/, /^lnkd\.in$/] },
  { key: 'youtube',   name: 'YouTube',   icon: '▶️',  hosts: [/(^|\.)youtube\.com$/, /^youtu\.be$/] },
  { key: 'vk',        name: 'VK',        icon: '👥', hosts: [/(^|\.)vk\.(com|ru)$/] },
  { key: 'odnoklassniki', name: 'OK',    icon: '👥', hosts: [/(^|\.)ok\.ru$/] },

  // ── portfolio ──
  { key: 'behance',   name: 'Behance',   icon: '🎨', hosts: [/(^|\.)behance\.net$/] },
  { key: 'dribbble',  name: 'Dribbble',  icon: '🏀', hosts: [/(^|\.)dribbble\.com$/] },
  { key: 'pinterest', name: 'Pinterest', icon: '📌', hosts: [/(^|\.)pinterest\.[a-z.]+$/] },
  { key: 'houzz',     name: 'Houzz',     icon: '🏠', hosts: [/(^|\.)houzz\.[a-z.]+$/] },
];

// Extracts the bare host from anything a user might paste: with or without a
// scheme, with or without "www.", with or without a path.
function hostOf(raw) {
  return String(raw || '')
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/^www\./i, '')
    .split(/[/?#]/)[0]
    .toLowerCase();
}

// The account name inside a platform URL — "t.me/gev" → "gev". Shown alongside
// the platform so two Telegram links stay distinguishable.
function handleOf(raw) {
  const path = String(raw || '')
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .split(/[?#]/)[0]
    .split('/').slice(1).filter(Boolean);
  if (!path.length) return '';
  // Skip routing segments that aren't the account itself.
  const skip = /^(in|profile|user|users|channel|c|p|pages|company|watch|send|invite)$/i;
  const seg = path.find(s => !skip.test(s)) || path[path.length - 1];
  return seg.replace(/^@/, '').slice(0, 28);
}

/**
 * Identifies a contact value.
 * Returns { key, name, icon, handle, isKnown } — `isKnown` false means it's a
 * plain link with no recognised service, and the caller should fall back to
 * showing the host.
 */
export function detectPlatform(value, type) {
  const raw = String(value || '').trim();

  // An explicitly-typed phone row, or a tel: link.
  if (type === 'phone' || /^tel:/i.test(raw)) {
    return { key: 'phone', name: 'Phone', icon: '📞', handle: raw.replace(/^tel:/i, ''), isKnown: true };
  }
  if (/^mailto:/i.test(raw) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
    return { key: 'email', name: 'Email', icon: '✉️', handle: raw.replace(/^mailto:/i, ''), isKnown: true };
  }

  const host = hostOf(raw);
  for (const p of PLATFORMS) {
    if (p.hosts.some(rx => rx.test(host))) {
      return { key: p.key, name: p.name, icon: p.icon, handle: handleOf(raw), isKnown: true };
    }
  }
  return { key: 'link', name: '', icon: '🔗', handle: '', isKnown: false };
}

/** Href for a contact — phones become tel:, everything else keeps its URL. */
export function contactHref(value, type) {
  const raw = String(value || '').trim();
  if (type === 'phone' || /^tel:/i.test(raw)) return `tel:${raw.replace(/^tel:/i, '').replace(/\s+/g, '')}`;
  if (/^mailto:/i.test(raw)) return raw;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return `mailto:${raw}`;
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
}
